import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import MenuEditorHeader from '../menuEditor/MenuEditorHeader'
import useFetch from '../hooks/useFetch'
import { getFullMenu, postUpdateMenu } from '../config/apiRoutes'
import { MenuCategory, MenuCategorySearchType, SerializedMenuCategory, defaultMenuCategoryTranslations, deserializeMenuCategories, generateCategorySearch } from '../models/MenuCategory'
import MenuCategoryEditor from '../menuEditor/MenuCategoryEditor'
import { generatePostSaveMenuDto, postSaveMenuDto } from '../models/UpdateMenuDto'
import SnackbarContext from '../contexts/SnackbarContext'
import { useTranslation } from 'react-i18next'
import UserContext from '../contexts/UserContext'
import PageContainer from '../containers/PageContainer'
import PageTitleCard from '../cards/PageTitleCard'
import PageTitleText from '../texts/PageTitleText'
import SearchInput from '../inputs/SearchInput'
import { newRestaurantRoute, usePathReconstructor } from '../config/pageRoutes'
import { useNavigate } from 'react-router'
import ResponsiveModal from '../modal/ResponsiveModal'
import SmallButton from '../buttons/SmallButton'

export enum MenuEditorMode {
  CATEGORIES = "categories",
  MENU = "menu",
  PHOTOS = "photos",
}

type Props = {}

const MenuEditorPage = (props: Props) => {
  const { call, error, errorMessage, loading } = useFetch();
  const [editedMenuCategories, setEditedMenuCategories] = useState<MenuCategory[]>([]);
  const { showMessage } = useContext(SnackbarContext);
  const { t } = useTranslation();
  const { selectedRestaurant, restaurantSettings } = useContext(UserContext);
  const [search, setSearch] = useState("");
  const allowReorder = !search;
  const { copyParamsFromPath } = usePathReconstructor();
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timer>();
  const [dataChangedModalOpen, setDataChangedModalOpen] = useState(false);
  const oldMenuCategories = useRef<MenuCategory[]>([]);
  const newMenuCategories = useRef<MenuCategory[]>([]);
  const turnOffDataChangedWarning = useRef(false);
  const [menuVersionTimestamp, setMenuVersionTimestamp] = useState<number>(Date.now());

  useEffect(() => {
    document.title = `QuickPick - ${t("navigation.menu_editor")}`;
  }, [t("navigation.menu_editor")]);

  useEffect(() => {
    if (restaurantSettings.length === 0) {
      navigate(copyParamsFromPath(newRestaurantRoute.path));
    }
  }, [restaurantSettings]);

  const displayedCategories = useMemo(() => {
    return editedMenuCategories.map(category => generateCategorySearch(category, search)).filter(category => category.searchType !== MenuCategorySearchType.NONE);
  }, [editedMenuCategories, search]);

  useEffect(() => {
    requestMenu();
    if (selectedRestaurant) {
      intervalRef.current = setInterval(() => {
        checkRequestMenu();
      }, 1 * 60 * 1000); //every 1 minute
    }
    return () => clearInterval(intervalRef.current);
  }, [selectedRestaurant?.restaurantShortName]);


  async function requestMenu() {
    if (!selectedRestaurant) {
      return;
    }
    const response = await call<undefined, SerializedMenuCategory[]>("GET", getFullMenu.replace(":restaurantShortName", selectedRestaurant.restaurantShortName));
    if (response.ok && response.data) {
      const deserialized = deserializeMenuCategories(response.data);

      setEditedMenuCategories(deserialized);
      oldMenuCategories.current = deserialized;
    }
  }

  async function checkRequestMenu() {
    if (!selectedRestaurant) {
      return;
    }
    if (turnOffDataChangedWarning.current) {
      return;
    }
    if (dataChangedModalOpen) {
      return;
    }
    const response = await call<undefined, SerializedMenuCategory[]>("GET", getFullMenu.replace(":restaurantShortName", selectedRestaurant.restaurantShortName));
    if (response.ok && response.data) {
      const deserialized = deserializeMenuCategories(response.data)
      const isDataChanged = JSON.stringify(deserialized) !== JSON.stringify(oldMenuCategories.current);
      if (isDataChanged) {
        newMenuCategories.current = deserialized;
        setDataChangedModalOpen(true);
      }
    }
  }

  async function saveChanges() {
    if (!selectedRestaurant) {
      return;
    }
    const updateData: postSaveMenuDto = generatePostSaveMenuDto(editedMenuCategories);
    const response = await call<postSaveMenuDto, undefined>("POST", postUpdateMenu.replace(":restaurantShortName", selectedRestaurant.restaurantShortName), updateData);
    if (response.ok) {
      showMessage(t("menu.menu_saved"), "success");
      requestMenu();
    }
  }

  useEffect(() => {
    if (error) {
      showMessage(errorMessage, "error");
    }
  }, [error]);

  function onNewCategoryClick() {
    const newOrder = editedMenuCategories.length > 0 ? editedMenuCategories[editedMenuCategories.length - 1].order + 1 : 1;
    const newCategory: MenuCategory = {
      id: (new Date()).getTime() * -1,
      isActive: false,
      order: newOrder,
      restaurantShortName: selectedRestaurant?.restaurantShortName || "",
      translations: defaultMenuCategoryTranslations,
      menuItems: []
    }
    setEditedMenuCategories([...editedMenuCategories, newCategory]);
  }

  function handleRefreshDataChange() {
    setEditedMenuCategories(newMenuCategories.current);
    oldMenuCategories.current = newMenuCategories.current;
    turnOffDataChangedWarning.current = false;
    setDataChangedModalOpen(false);
    setMenuVersionTimestamp(Date.now());
  }

  function handleDataChangedCancel() {
    turnOffDataChangedWarning.current = true;
    setDataChangedModalOpen(false);
  }

  return (
    <>
      <PageContainer>
        <PageTitleCard>
          <div className="flex justify-between" >
            <PageTitleText>
              {t("menu.menu_editor")}
            </PageTitleText>
            <div className="mt-1">
              <SearchInput
                placeholder={t(`common.search`)}
                onSearch={setSearch}
              />
            </div>
          </div>
        </PageTitleCard>
        <MenuEditorHeader
          onSave={saveChanges}
          onNewCategoryClick={onNewCategoryClick}
          reloadMenu={requestMenu}
        />
        {!allowReorder && (
          <div className="text-sm text-utility-mutedText text-center py-1">
            {t("menu.reorder_disabled_search")}
          </div>
        )}
        <div>
          <MenuCategoryEditor
            key={menuVersionTimestamp}
            searchedCategories={displayedCategories}
            categories={editedMenuCategories}
            setCategories={setEditedMenuCategories}
            isReorderAllowed={allowReorder}
          />
          {!loading && displayedCategories.length === 0 && (
            <div className="text-center mt-2">
              {t("common.no_results")}
            </div>
          )}
        </div>
      </PageContainer>
      <ResponsiveModal
        open={dataChangedModalOpen}
        handleClose={handleDataChangedCancel}
      >
        <>
          <div className="text-center font-bold text-[22px] mb-2 text-primary">
            {t("common.datachange_detected")}
          </div>
          <div className="mb-2">
            {t("common.datachange_subtitle")}
          </div>
          <div className="mb-4">
            {t("common.datachange_description")}
          </div>
          <div className="md:grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <SmallButton
                variant="strike"
                onClick={handleRefreshDataChange}
                fullWidth
              >
                {t("common.datachange_refresh")}
              </SmallButton>
            </div>
            <div className="col-span-1">
              <SmallButton
                onClick={handleDataChangedCancel}
                fullWidth
              >
                {t("common.datachange_overwrite")}
              </SmallButton>
            </div>
          </div>
        </>
      </ResponsiveModal>
    </>
  )
}

export default MenuEditorPage